import { getDefaultMetricsDateRange } from "@redotech/redo-merchant-marketing-app/automations/utils/metrics-date-range";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";

export interface MetricsDateRangeValue {
  dateRange: [Temporal.PlainDate, Temporal.PlainDate];
  setDateRange: (
    range: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
}

const AutomationMetricsDateRangeContext = createContext<
  MetricsDateRangeValue | undefined
>(undefined);

export function AutomationMetricsDateRangeProvider({
  children,
  initialDateRange,
  onDateRangeChange,
}: {
  children: ReactNode | ReactNode[];
  initialDateRange?: [Temporal.PlainDate, Temporal.PlainDate];
  onDateRangeChange?: (range: [Temporal.PlainDate, Temporal.PlainDate]) => void;
}) {
  const [dateRange, setDateRangeState] = useState<
    [Temporal.PlainDate, Temporal.PlainDate]
  >(initialDateRange ?? getDefaultMetricsDateRange());

  const value = useMemo<MetricsDateRangeValue>(
    () => ({
      dateRange,
      setDateRange: (range) => {
        const next = range ?? getDefaultMetricsDateRange();
        setDateRangeState(next);
        onDateRangeChange?.(next);
      },
    }),
    [dateRange, onDateRangeChange],
  );

  return (
    <AutomationMetricsDateRangeContext.Provider value={value}>
      {children}
    </AutomationMetricsDateRangeContext.Provider>
  );
}

/**
 * Read the optional metrics date range scoped to the surrounding marketing
 * automation editor. Returns undefined when no provider is mounted (e.g. when
 * a metrics-aware node is rendered outside of the marketing flow editor).
 */
export function useOptionalAutomationMetricsDateRange():
  | MetricsDateRangeValue
  | undefined {
  return useContext(AutomationMetricsDateRangeContext);
}
